import { useEffect } from 'react';

/**
 * Lightweight SEO helper — injects per-page <title>, meta description, OG tags,
 * canonical URL, and (optionally) a JSON-LD schema block into <head>.
 *
 * Cleans up on unmount so one page's tags don't leak into another.
 *
 * Usage:
 *   <SEO
 *     title="Girl with Balloon by Banksy | Zebra One Gallery"
 *     description="Signed screenprint, 2004 edition..."
 *     image="https://.../img.jpg"
 *     canonical="https://.../artwork/abc"
 *     schema={{ "@context": "https://schema.org", "@type": "VisualArtwork", ... }}
 *   />
 */
const SEO = ({ title, description, image, canonical, schema, keywords }) => {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const tags = [];
    const upsertMeta = (attr, key, content) => {
      if (!content) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      const created = !el;
      if (created) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute('content');
      el.setAttribute('content', content);
      tags.push({ el, prev, created });
    };

    upsertMeta('name', 'description', description);
    upsertMeta('name', 'keywords', keywords);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:image', image);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:type', 'article');
    upsertMeta('name', 'twitter:card', image ? 'summary_large_image' : 'summary');
    upsertMeta('name', 'twitter:site', '@zebraonegallery');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', image);

    // Canonical link
    let canonicalEl = null;
    let canonicalPrev = null;
    let canonicalCreated = false;
    if (canonical) {
      canonicalEl = document.head.querySelector('link[rel="canonical"]');
      if (!canonicalEl) {
        canonicalEl = document.createElement('link');
        canonicalEl.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalEl);
        canonicalCreated = true;
      } else {
        canonicalPrev = canonicalEl.getAttribute('href');
      }
      canonicalEl.setAttribute('href', canonical);
    }

    // JSON-LD schema (page-scoped; tagged so we remove only ours)
    let scriptEl = null;
    if (schema) {
      scriptEl = document.createElement('script');
      scriptEl.type = 'application/ld+json';
      scriptEl.dataset.seoPage = '1';
      scriptEl.textContent = JSON.stringify(schema);
      document.head.appendChild(scriptEl);
    }

    return () => {
      document.title = prevTitle;
      tags.forEach(({ el, prev, created }) => {
        if (created) el.remove();
        else if (prev !== null) el.setAttribute('content', prev);
      });
      if (canonicalEl) {
        if (canonicalCreated) canonicalEl.remove();
        else if (canonicalPrev !== null) canonicalEl.setAttribute('href', canonicalPrev);
      }
      if (scriptEl) scriptEl.remove();
    };
  }, [title, description, image, canonical, schema, keywords]);

  return null;
};

export default SEO;
